/**
 * Client-side form scoring (mirrors utils/formScoring.js).
 */

import {
  ANSWER_MODES,
  CHOICE_QUESTION_TYPES,
  TEXT_QUESTION_TYPES,
  NUMBER_QUESTION_TYPES,
  DATE_QUESTION_TYPES,
  UPLOAD_QUESTION_TYPES,
  FORM_TYPES,
  defaultScoringForQuestion,
  defaultNumberRule,
  clampAnswerModeForQuestionType
} from '../constants/formScoring';
import { stripHtmlToText } from './richText';

export { FORM_TYPES, ANSWER_MODES, defaultScoringForQuestion };

export function isPointsForm(formType) {
  return String(formType || '').toLowerCase() === FORM_TYPES.POINTS;
}

function normalizeNumberRule(raw) {
  const rule = raw && typeof raw === 'object' ? raw : {};
  return {
    exact: rule.exact != null && rule.exact !== '' ? Number(rule.exact) : null,
    min: rule.min != null && rule.min !== '' ? Number(rule.min) : null,
    max: rule.max != null && rule.max !== '' ? Number(rule.max) : null
  };
}

function parseDateValue(value) {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (iso) {
    const mm = iso[2].padStart(2, '0');
    const dd = iso[3].padStart(2, '0');
    return `${iso[1]}-${mm}-${dd}`;
  }
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const mm = mdy[1].padStart(2, '0');
    const dd = mdy[2].padStart(2, '0');
    return `${mdy[3]}-${mm}-${dd}`;
  }
  return null;
}

function normalizeDateRule(raw) {
  const rule = raw && typeof raw === 'object' ? raw : {};
  return {
    exact: parseDateValue(rule.exact),
    min: parseDateValue(rule.min),
    max: parseDateValue(rule.max)
  };
}

function normalizeScoring(question) {
  const base = defaultScoringForQuestion(question);
  const scoring = question.scoring && typeof question.scoring === 'object' ? question.scoring : {};
  const answerMode = clampAnswerModeForQuestionType(question.type, scoring.answerMode || base.answerMode);
  const storedNumber = scoring.numberRule && typeof scoring.numberRule === 'object' ? scoring.numberRule : {};
  const storedDate = scoring.dateRule && typeof scoring.dateRule === 'object' ? scoring.dateRule : {};
  return {
    answerMode,
    correctOptions: Array.isArray(scoring.correctOptions)
      ? scoring.correctOptions.map(String).filter(Boolean)
      : [],
    textRule: {
      match: scoring.textRule?.match === 'equals' ? 'equals' : 'includes',
      value: String(scoring.textRule?.value ?? ''),
      caseSensitive: scoring.textRule?.caseSensitive === true
    },
    numberRule: {
      exact: storedNumber.exact ?? '',
      min: storedNumber.min ?? '',
      max: storedNumber.max ?? ''
    },
    dateRule: {
      exact: storedDate.exact ?? '',
      min: storedDate.min ?? '',
      max: storedDate.max ?? ''
    }
  };
}

function isEmptyAnswer(value) {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object' && value.name) return false;
  return false;
}

function scoreTextAnswer(value, textRule) {
  const text = String(value ?? '');
  const target = String(textRule?.value ?? '');
  if (!target) return false;
  const a = textRule.caseSensitive ? text : text.toLowerCase();
  const b = textRule.caseSensitive ? target : target.toLowerCase();
  if (textRule.match === 'equals') return a === b;
  return a.includes(b);
}

function parseNumericAnswer(value) {
  if (value == null || value === '') return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function scoreNumberAnswer(value, answerMode, numberRule) {
  const answer = parseNumericAnswer(value);
  if (!Number.isFinite(answer)) return false;
  const rule = normalizeNumberRule(numberRule);
  if (answerMode === ANSWER_MODES.NUMBER_EXACT) {
    return Number.isFinite(rule.exact) && answer === rule.exact;
  }
  if (answerMode === ANSWER_MODES.NUMBER_RANGE) {
    if (!Number.isFinite(rule.min) || !Number.isFinite(rule.max)) return false;
    const lo = Math.min(rule.min, rule.max);
    const hi = Math.max(rule.min, rule.max);
    return answer >= lo && answer <= hi;
  }
  return false;
}

function scoreDateAnswer(value, answerMode, dateRule) {
  const answer = parseDateValue(value);
  if (!answer) return false;
  const rule = normalizeDateRule(dateRule);
  if (answerMode === ANSWER_MODES.DATE_EXACT) {
    return rule.exact && answer === rule.exact;
  }
  if (answerMode === ANSWER_MODES.DATE_RANGE) {
    if (!rule.min || !rule.max) return false;
    const from = rule.min < rule.max ? rule.min : rule.max;
    const to = rule.min < rule.max ? rule.max : rule.min;
    return answer >= from && answer <= to;
  }
  return false;
}

function scoreMultipleChoice(value, correctOptions) {
  const selected = Array.isArray(value) ? value.map(String) : value ? [String(value)] : [];
  const selectedSet = new Set(selected);
  const correctSet = new Set(correctOptions.map(String));
  if (!correctSet.size) return false;
  const sameSize = selectedSet.size === correctSet.size;
  return sameSize && [...correctSet].every((c) => selectedSet.has(c));
}

function isAnswerCorrect(question, value, scoring) {
  if (scoring.answerMode === ANSWER_MODES.CHOICES) {
    if (!CHOICE_QUESTION_TYPES.has(question.type)) return false;
    if (question.type === 'multiple-choice') {
      return scoreMultipleChoice(value, scoring.correctOptions);
    }
    return scoring.correctOptions.map(String).includes(String(value ?? ''));
  }
  if (scoring.answerMode === ANSWER_MODES.TEXT) {
    return TEXT_QUESTION_TYPES.has(question.type) && scoreTextAnswer(value, scoring.textRule);
  }
  if (scoring.answerMode === ANSWER_MODES.NUMBER_EXACT || scoring.answerMode === ANSWER_MODES.NUMBER_RANGE) {
    return NUMBER_QUESTION_TYPES.has(question.type) && scoreNumberAnswer(value, scoring.answerMode, scoring.numberRule);
  }
  if (scoring.answerMode === ANSWER_MODES.DATE_EXACT || scoring.answerMode === ANSWER_MODES.DATE_RANGE) {
    return DATE_QUESTION_TYPES.has(question.type) && scoreDateAnswer(value, scoring.answerMode, scoring.dateRule);
  }
  return false;
}

function resolveEarnedPoints(question, value, correct, maxPoints) {
  if (correct) {
    return { earned: maxPoints, max: maxPoints, correct: true };
  }
  if (isEmptyAnswer(value)) {
    return { earned: 0, max: maxPoints, correct: false };
  }
  const attemptPts = Math.max(0, Number(question.attemptPoints) || 0);
  if (question.awardAttemptPoints === true && attemptPts > 0) {
    return { earned: attemptPts, max: maxPoints, correct: false };
  }
  return { earned: 0, max: maxPoints, correct: false };
}

function scoreUploadFreeAnswer(question, value, maxPoints) {
  if (!isEmptyAnswer(value)) {
    return { earned: maxPoints, max: maxPoints, correct: true };
  }
  if (question.required) {
    return { earned: 0, max: maxPoints, correct: false };
  }
  if (question.awardAttemptPoints === true) {
    const attemptPts = Math.max(0, Number(question.attemptPoints) || 0);
    if (attemptPts > 0) {
      return { earned: attemptPts, max: maxPoints, correct: false };
    }
  }
  return { earned: maxPoints, max: maxPoints, correct: true };
}

function scoreUploadManualAnswer(question, value, maxPoints) {
  if (question.required && isEmptyAnswer(value)) {
    return { earned: 0, max: maxPoints, correct: false, manual: true };
  }
  if (!isEmptyAnswer(value) && question.awardAttemptPoints === true) {
    const attemptPts = Math.max(0, Number(question.attemptPoints) || 0);
    return {
      earned: attemptPts,
      max: maxPoints,
      correct: false,
      manual: true,
      pendingReview: true
    };
  }
  return {
    earned: null,
    max: maxPoints,
    correct: null,
    manual: true,
    pendingReview: !isEmptyAnswer(value)
  };
}

export function scoreQuestion(question, value) {
  const scoring = normalizeScoring(question);
  const maxPoints = Math.max(0, Number(question.points) || 0);
  const isUpload = UPLOAD_QUESTION_TYPES.has(question.type);

  if (scoring.answerMode === ANSWER_MODES.MANUAL) {
    if (isUpload) {
      return scoreUploadManualAnswer(question, value, maxPoints);
    }
    if (question.required && isEmptyAnswer(value)) {
      return { earned: 0, max: maxPoints, correct: false, manual: true };
    }
    return {
      earned: null,
      max: maxPoints,
      correct: null,
      manual: true,
      pendingReview: !isEmptyAnswer(value)
    };
  }

  if (scoring.answerMode === ANSWER_MODES.FREE) {
    if (isUpload) {
      return scoreUploadFreeAnswer(question, value, maxPoints);
    }
    if (question.required && isEmptyAnswer(value)) {
      return { earned: 0, max: maxPoints, correct: false };
    }
    return { earned: maxPoints, max: maxPoints, correct: true };
  }

  const correct = isAnswerCorrect(question, value, scoring);
  return resolveEarnedPoints(question, value, correct, maxPoints);
}

export function scoreSubmission(formType, questions, answers) {
  if (!isPointsForm(formType) || !Array.isArray(questions)) return null;

  let score = 0;
  let maxScore = 0;
  const breakdown = [];

  for (const question of questions) {
    if (!question?.question_id) continue;
    const value = answers[question.question_id];
    const result = scoreQuestion(question, value);
    if (Number.isFinite(result.earned)) {
      score += result.earned;
    }
    maxScore += result.max;
    breakdown.push({
      questionId: question.question_id,
      label: stripHtmlToText(question.label) || 'Untitled',
      ...result
    });
  }

  return {
    score: Math.round(score * 100) / 100,
    maxScore: Math.round(maxScore * 100) / 100,
    breakdown
  };
}

export function validateQuestionScoring(question, formType) {
  if (!isPointsForm(formType)) return null;

  const points = Number(question.points);
  if (!Number.isFinite(points) || points < 0) {
    return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have points ≥ 0`;
  }

  const scoring = normalizeScoring(question);
  if (scoring.answerMode === ANSWER_MODES.CHOICES) {
    if (!CHOICE_QUESTION_TYPES.has(question.type)) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" uses choice scoring but is not a choice field`;
    }
    if (!scoring.correctOptions.length) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must mark at least one correct option (or use Free answer)`;
    }
  }
  if (scoring.answerMode === ANSWER_MODES.TEXT) {
    if (!TEXT_QUESTION_TYPES.has(question.type)) {
      return null;
    }
    if (!String(scoring.textRule.value || '').trim()) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have match text (or use Free answer)`;
    }
  }
  if (scoring.answerMode === ANSWER_MODES.NUMBER_EXACT) {
    if (!NUMBER_QUESTION_TYPES.has(question.type)) return null;
    const rule = normalizeNumberRule(scoring.numberRule);
    if (!Number.isFinite(rule.exact)) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have an exact number (or use Free answer)`;
    }
  }
  if (scoring.answerMode === ANSWER_MODES.NUMBER_RANGE) {
    if (!NUMBER_QUESTION_TYPES.has(question.type)) return null;
    const rule = normalizeNumberRule(scoring.numberRule);
    if (!Number.isFinite(rule.min) || !Number.isFinite(rule.max)) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have min and max values (or use Free answer)`;
    }
    if (rule.max <= rule.min) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have max greater than min`;
    }
  }
  if (scoring.answerMode === ANSWER_MODES.DATE_EXACT) {
    if (!DATE_QUESTION_TYPES.has(question.type)) return null;
    const rule = normalizeDateRule(scoring.dateRule);
    if (!rule.exact) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have an exact date (or use Free answer)`;
    }
  }
  if (scoring.answerMode === ANSWER_MODES.DATE_RANGE) {
    if (!DATE_QUESTION_TYPES.has(question.type)) return null;
    const rule = normalizeDateRule(scoring.dateRule);
    if (!rule.min || !rule.max) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have from and to dates (or use Free answer)`;
    }
    if (rule.max <= rule.min) {
      return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have an end date after the start date`;
    }
  }
  if (question.awardAttemptPoints === true) {
    const attemptAllowed =
      UPLOAD_QUESTION_TYPES.has(question.type) ||
      (scoring.answerMode !== ANSWER_MODES.FREE && scoring.answerMode !== ANSWER_MODES.MANUAL);
    if (attemptAllowed) {
      const attemptPts = Number(question.attemptPoints);
      if (!Number.isFinite(attemptPts) || attemptPts < 0) {
        return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have attempt points ≥ 0`;
      }
      if (attemptPts === 0) {
        return `Question "${stripHtmlToText(question.label) || 'Untitled'}" must have attempt points > 0 when attempt scoring is enabled`;
      }
    }
  }
  return null;
}
